// custom-date-input.component.ts
import { Component, Input, forwardRef, ViewChild, ElementRef, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type DateValidationType = 
  | 'age-21-plus'      // Must be 21+ years old
  | 'age-under-21'     // Must be under 21 years old
  | 'future-only'      // Only future dates (from tomorrow)
  | 'past-only'        // Only past dates (until yesterday)
  | 'custom'           // Use custom minDate/maxDate
  | 'none';            // No restrictions

@Component({
  selector: 'app-custom-date-input',
  templateUrl: './custom-date-input.component.html',
  styleUrls: ['./custom-date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDateInputComponent),
      multi: true
    }
  ]
})
export class CustomDateInputComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder: string = 'اختر التاريخ (يوم/شهر/سنة)';
  @Input() validationType: DateValidationType = 'none';
  
  // Optional: for 'custom' validation type
  @Input() minDate?: Date;
  @Input() maxDate?: Date;
  
  @Input() disabled: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() backgroundColor: string = 'white';

  @ViewChild('datePicker') datePicker!: ElementRef<HTMLInputElement>;
  @ViewChild('fakeInput') fakeInput!: ElementRef<HTMLInputElement>;

  displayValue: string = '';
  internalValue: string = '';
  errorMessage: string = '';

  minDateFormatted: string = '';
  maxDateFormatted: string = '';

  private onChange: (value: string) => void = () => {};
  private onTouchedCallback: () => void = () => {};

  ngOnChanges(): void {
    this.calculateDateLimits();
  }

  private calculateDateLimits(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (this.validationType) {
      case 'age-21-plus':
        const date21YearsAgo = new Date(today);
        date21YearsAgo.setFullYear(today.getFullYear() - 21);
        this.maxDateFormatted = this.formatDateForInput(date21YearsAgo);
        this.minDateFormatted = '';
        break;

      case 'age-under-21':
        const minDate21 = new Date(today);
        minDate21.setFullYear(today.getFullYear() - 21);
        minDate21.setDate(minDate21.getDate() + 1);
        this.minDateFormatted = this.formatDateForInput(minDate21);
        this.maxDateFormatted = this.formatDateForInput(today);
        break;

      case 'future-only':
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        this.minDateFormatted = this.formatDateForInput(tomorrow);
        this.maxDateFormatted = '';
        break;

      case 'past-only':
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        this.maxDateFormatted = this.formatDateForInput(yesterday);
        this.minDateFormatted = '';
        break;

      case 'custom':
        this.minDateFormatted = this.minDate ? this.formatDateForInput(this.minDate) : '';
        this.maxDateFormatted = this.maxDate ? this.formatDateForInput(this.maxDate) : '';
        break;

      case 'none':
      default:
        this.minDateFormatted = '';
        this.maxDateFormatted = '';
        break;
    }
  }

  // ─── ControlValueAccessor Methods ─────────────────────────────────────
  writeValue(value: string | null): void {
    if (value) {
      this.internalValue = value;
      this.displayValue = this.formatToDisplay(value);
    } else {
      this.internalValue = '';
      this.displayValue = '';
    }
    this.errorMessage = '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  openDatePicker(): void {
    if (this.disabled || this.readOnly || !this.datePicker?.nativeElement) {
      return;
    }
    
    const inputEl = this.datePicker.nativeElement as any;
    
    if (this.internalValue) {
      inputEl.value = this.internalValue;
    }
    
    if (typeof inputEl.showPicker === 'function') {
      try {
        inputEl.showPicker();
      } catch (e) {
        inputEl.focus();
        inputEl.click();
      }
    } else {
      inputEl.focus();
      inputEl.click();
    }
  }

  onDateSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectedDate = input.value;

    if (selectedDate) {
      this.internalValue = selectedDate;
      this.displayValue = this.formatToDisplay(selectedDate);
      this.errorMessage = '';
      this.onChange(this.internalValue);
    } else {
      this.internalValue = '';
      this.displayValue = '';
      this.onChange('');
    }

    this.onTouchedCallback();
  }

  // ─── Manual Input Handling ─────────────────────────────────────────────
  onManualInput(event: Event): void {
    if (this.readOnly || this.disabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove non-numeric characters except / and -
    value = value.replace(/[^\d/-]/g, '');

    // Auto-format as user types
    value = this.autoFormatInput(value);

    // Update display
    this.displayValue = value;
    input.value = value;

    // Clear error while typing
    this.errorMessage = '';
  }

  onManualInputBlur(event: Event): void {
    if (this.readOnly || this.disabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (!value) {
      this.internalValue = '';
      this.displayValue = '';
      this.errorMessage = '';
      this.onChange('');
      this.onTouchedCallback();
      return;
    }

    // Parse and validate the input
    const parsed = this.parseManualDate(value);
    
    if (parsed.isValid && parsed.isoDate) {
      // Validate against min/max
      const isWithinRange = this.validateDateRange(parsed.isoDate);
      
      if (isWithinRange) {
        this.internalValue = parsed.isoDate;
        this.displayValue = this.formatToDisplay(parsed.isoDate);
        this.errorMessage = '';
        this.onChange(this.internalValue);
      } else {
        this.errorMessage = 'التاريخ خارج النطاق المسموح';
        this.displayValue = value;
      }
    } else {
      this.errorMessage = 'صيغة التاريخ غير صحيحة (يوم/شهر/سنة)';
      this.displayValue = value;
    }

    this.onTouchedCallback();
  }

  // ─── Auto-format as User Types ─────────────────────────────────────────
  private autoFormatInput(value: string): string {
    const cleaned = value.replace(/\//g, '');
    
    if (cleaned.length === 0) return '';
    
    let formatted = '';
    
    if (cleaned.length >= 1) {
      formatted = cleaned.substring(0, 2);
    }
    
    if (cleaned.length >= 3) {
      formatted += '/' + cleaned.substring(2, 4);
    }
    
    if (cleaned.length >= 5) {
      formatted += '/' + cleaned.substring(4, 8);
    }
    
    return formatted;
  }

  // ─── Parse Manual Date Input ───────────────────────────────────────────
  private parseManualDate(input: string): { isValid: boolean; isoDate?: string } {
    const patterns = [
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,
    ];

    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);

        if (month < 1 || month > 12) {
          return { isValid: false };
        }

        if (day < 1 || day > 31) {
          return { isValid: false };
        }

        const testDate = new Date(year, month - 1, day);
        if (
          testDate.getDate() !== day ||
          testDate.getMonth() !== month - 1 ||
          testDate.getFullYear() !== year
        ) {
          return { isValid: false };
        }

        const isoDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return { isValid: true, isoDate };
      }
    }

    return { isValid: false };
  }

  // ─── Validate Date Range ───────────────────────────────────────────────
  private validateDateRange(isoDate: string): boolean {
    const date = new Date(isoDate);
    
    if (this.minDateFormatted) {
      const minDate = new Date(this.minDateFormatted);
      if (date < minDate) return false;
    }
    
    if (this.maxDateFormatted) {
      const maxDate = new Date(this.maxDateFormatted);
      if (date > maxDate) return false;
    }
    
    return true;
  }

  // ─── Clear Error on Focus ──────────────────────────────────────────────
  onFocus(): void {
    this.errorMessage = '';
  }

  // ─── Helper Methods ────────────────────────────────────────────────────
  private formatToDisplay(isoDate: string): string {
    if (!isoDate) return '';

    try {
      const [year, month, day] = isoDate.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return '';
    }
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}